import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Eye, Palette, Calendar, User, DollarSign, Search, Star, ChevronDown, Award, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SafeImage } from '@/components/ui/safe-image';
import { useGalleryArtworks } from '@/hooks/useGalleryArtworks';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface GalleryArtwork {
  id: string;
  title: string;
  artistName: string;
  artistBio?: string;
  artistYears?: string;
  year: number;
  image: string;
  additional_images?: string[] | null;
  description: string;
  medium?: string;
  dimensions?: string;
  estimate_low?: number;
  estimate_high?: number;
}

const INITIAL_ITEMS = 15;
const LOAD_MORE_ITEMS = 30;

const LivingGallery = () => {
  const { data: allArtworks, isLoading, error } = useGalleryArtworks();
  const [searchTerm, setSearchTerm] = useState('');
  const [displayCount, setDisplayCount] = useState(INITIAL_ITEMS);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState<GalleryArtwork | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Filter artworks
  const filteredArtworks = allArtworks?.filter((artwork: GalleryArtwork) => {
    const matchesSearch = artwork.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         artwork.artistName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }) || [];

  // Get currently displayed artworks
  const displayedArtworks = filteredArtworks.slice(0, displayCount);
  const hasMore = displayCount < filteredArtworks.length;

  // Reset display count when search changes
  useEffect(() => {
    setDisplayCount(INITIAL_ITEMS);
  }, [searchTerm]);

  // Load more artworks
  const loadMore = async () => {
    setIsLoadingMore(true);
    await new Promise(resolve => setTimeout(resolve, 300)); // Faster loading
    setDisplayCount(prev => Math.min(prev + LOAD_MORE_ITEMS, filteredArtworks.length));
    setIsLoadingMore(false);
  };

  // Format currency for estimates
  const formatEstimate = (low?: number, high?: number) => {
    if (!low && !high) return null;
    if (low && high) return `${formatCurrency(low)} - ${formatCurrency(high)}`;
    if (low) return `Starting at ${formatCurrency(low)}`;
    if (high) return `Up to ${formatCurrency(high)}`;
    return null;
  };

  // Get all images for an artwork (main + additional)
  const getAllImages = (artwork: GalleryArtwork): string[] => {
    const images: string[] = [];
    
    // Add main image if it exists
    if (artwork.image) {
      images.push(artwork.image);
    }
    
    // Add additional images if they exist
    if (artwork.additional_images) {
      try {
        let additionalImages: any = artwork.additional_images;
        
        // Handle different data formats
        if (typeof additionalImages === 'string') {
          additionalImages = JSON.parse(additionalImages);
        }
        
        if (Array.isArray(additionalImages)) {
          // Filter out any null/empty values and add to images
          const validImages = additionalImages.filter(img => img && typeof img === 'string' && img.trim() !== '');
          images.push(...validImages);
        }
      } catch (error) {
        console.warn('Error parsing additional images:', error);
      }
    }
    
    // Remove duplicates and return
    return [...new Set(images)].filter(Boolean);
  };

  // Handle opening artwork popup
  const openArtworkPopup = (artwork: GalleryArtwork) => {
    console.log('🖼️ Opening artwork popup for:', artwork.title);
    console.log('📸 Main image:', artwork.image);
    console.log('📁 Additional images raw:', artwork.additional_images);
    
    const allImages = getAllImages(artwork);
    console.log('🎨 All processed images:', allImages);
    
    setSelectedArtwork(artwork);
    setSelectedImageIndex(0); // Reset to first image
  };

  // Navigate between images in popup
  const navigateImage = (direction: 'prev' | 'next', totalImages: number) => {
    if (direction === 'prev') {
      setSelectedImageIndex(prev => prev > 0 ? prev - 1 : totalImages - 1);
    } else {
      setSelectedImageIndex(prev => prev < totalImages - 1 ? prev + 1 : 0);
    }
  };

  if (isLoading) {
    return (
      <section className="w-full min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="w-full relative z-10 py-16 px-2 md:px-4">
          <div className="text-center mb-20">
            <Skeleton className="h-12 w-80 mx-auto mb-8 bg-white/10" />
            <Skeleton className="h-20 w-96 mx-auto mb-6 bg-white/10" />
            <Skeleton className="h-6 w-[600px] mx-auto mb-12 bg-white/10" />
            <div className="flex justify-center gap-6">
              <Skeleton className="h-12 w-96 bg-white/10" />
              <Skeleton className="h-12 w-32 bg-white/10" />
            </div>
          </div>
          <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 xl:columns-5 2xl:columns-6 gap-3 md:gap-4 space-y-3 md:space-y-4">
            {[...Array(50)].map((_, i) => (
              <Skeleton key={i} className={`w-full ${i % 4 === 0 ? 'h-96' : i % 4 === 1 ? 'h-80' : i % 4 === 2 ? 'h-72' : 'h-88'} mb-4 break-inside-avoid bg-white/10`} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error || !allArtworks?.length) {
    return (
      <section className="w-full min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="w-full relative z-10 py-16 px-2 md:px-4">
          <div className="text-center">
            <Palette className="w-24 h-24 text-blue-400 mx-auto mb-8" />
            <h2 className="text-4xl font-light text-white mb-4 tracking-wide">Gallery Temporarily Unavailable</h2>
            <p className="text-white/70 text-lg font-light">We're working on bringing you the most beautiful collection of Florida Highwaymen art.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 cv-auto">
      {/* Sophisticated Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/3 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/3 rounded-full blur-3xl" />
      </div>

      {/* Professional pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.01]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='20' cy='20' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px'
        }}>
      </div>

      <div className="w-full relative z-10 py-16 px-2 md:px-4">
        {/* Professional Header Section */}
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Professional Badge */}
          <div className="inline-flex items-center justify-center px-8 py-3 mb-8 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-lg">
            <Award className="h-5 w-5 mr-3 text-emerald-400" />
            <span className="text-white font-light tracking-wide">Curated Collection</span>
          </div>

          {/* Professional Main Title */}
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 text-white tracking-tight leading-tight">
            <span className="block">Gallery of</span>
            <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-blue-400 bg-clip-text text-transparent font-light">
              Masters
            </span>
          </h2>
          
          {/* Professional Subtitle */}
          <p className="text-2xl text-white/70 max-w-5xl mx-auto leading-relaxed font-light mb-12">
            Discover the breathtaking legacy of the <span className="text-emerald-300 font-normal">Florida Highwaymen</span> through 
            this curated collection of original artworks that changed American art forever
          </p>

          {/* Search Controls */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-6 justify-center items-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 w-5 h-5" />
              <Input
                placeholder="Search by artwork or artist..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 text-lg border-white/20 bg-white/10 backdrop-blur-xl text-white placeholder:text-white/60 focus:border-emerald-400/50 focus:ring-emerald-400/20 rounded-xl shadow-lg font-light tracking-wide"
              />
            </div>
            <Badge className="bg-emerald-500/20 text-emerald-300 px-6 py-3 text-base font-light border border-emerald-400/30 backdrop-blur-xl shadow-lg tracking-wide">
              {filteredArtworks.length} Masterpieces
            </Badge>
          </motion.div>
        </motion.div>

        {/* Gallery - Simple Image Grid */}
        <motion.div 
          className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 xl:columns-5 2xl:columns-6 gap-3 md:gap-4 space-y-3 md:space-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <AnimatePresence>
            {displayedArtworks.map((artwork: GalleryArtwork, index: number) => {
              const isFeatured = index < 3;
              
              return (
                <motion.div
                  key={artwork.id}
                  className="break-inside-avoid mb-4"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ 
                    duration: 0.4,
                    delay: index * 0.02
                  }}
                >
                  <Card 
                    className="overflow-hidden border border-white/10 shadow-lg hover:shadow-xl transition-all duration-300 bg-white/5 backdrop-blur-xl cursor-pointer group hover:border-emerald-400/30"
                    onClick={() => openArtworkPopup(artwork)}
                  >
                    <div className="relative">
                      <SafeImage
                        src={artwork.image}
                        alt={artwork.title}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                      
                      {/* Simple overlay on hover */}
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <Eye className="w-8 h-8 text-white" />
                      </div>
                      
                      {/* Featured badge */}
                      {isFeatured && (
                        <div className="absolute top-3 left-3">
                          <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 shadow-lg backdrop-blur-xl">
                            <Star className="w-3 h-3 mr-1 fill-current" />
                            Featured
                          </Badge>
                        </div>
                      )}
                    </div>
                    
                    {/* Simple title overlay */}
                    <CardContent className="p-4">
                      <h3 className="font-light text-white text-sm leading-tight mb-1 tracking-wide">
                        {artwork.title}
                      </h3>
                      <p className="text-white/60 text-xs font-light">
                        {artwork.artistName} • {artwork.year}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {/* Load More Button */}
        {hasMore && (
          <motion.div 
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Button 
              onClick={loadMore}
              disabled={isLoadingMore}
              size="lg"
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white px-12 py-4 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50 font-light tracking-wide border-0 backdrop-blur-xl hover:scale-[1.02] hover:-translate-y-1"
            >
              {isLoadingMore ? (
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Loading...</span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span>Load Next 30 Masterpieces</span>
                  <ChevronDown className="w-5 h-5" />
                  <span className="text-sm opacity-75">({filteredArtworks.length - displayCount} remaining)</span>
                </div>
              )}
            </Button>
          </motion.div>
        )}

        {/* No Results */}
        {filteredArtworks.length === 0 && (
          <div className="text-center py-20">
            <Palette className="w-20 h-20 text-blue-400 mx-auto mb-6" />
            <h3 className="text-3xl font-light text-white mb-4 tracking-wide">No artworks found</h3>
            <p className="text-white/70 text-lg mb-8 font-light">Try adjusting your search to discover more masterpieces</p>
            <Button 
              onClick={() => setSearchTerm('')} 
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-light tracking-wide border-0 backdrop-blur-xl hover:scale-[1.02] hover:-translate-y-1"
            >
              View All Artworks
            </Button>
          </div>
        )}

        {/* Footer Stats */}
        <motion.div 
          className="text-center mt-24 pt-12 border-t border-white/10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-light text-white mb-2 tracking-wide">{displayedArtworks.length}</div>
              <div className="text-white/70 font-light tracking-wide">Currently Viewing</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-light text-white mb-2 tracking-wide">{filteredArtworks.length}</div>
              <div className="text-white/70 font-light tracking-wide">Total Masterpieces</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-light text-white mb-2 tracking-wide">70+</div>
              <div className="text-white/70 font-light tracking-wide">Years of Legacy</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Artwork Detail Popup */}
      <Dialog open={selectedArtwork !== null} onOpenChange={() => setSelectedArtwork(null)}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto bg-slate-950/95 backdrop-blur-xl border border-white/10">
          {selectedArtwork && (() => {
            const allImages = getAllImages(selectedArtwork);
            const currentImage = allImages[selectedImageIndex];
            const hasMultipleImages = allImages.length > 1;
            
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-light text-white pr-8 tracking-wide">
                    {selectedArtwork.title}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Image Gallery */}
                  <div className="space-y-4">
                    {/* Main Image */}
                    <div className="relative">
                      <SafeImage
                        src={currentImage}
                        alt={`${selectedArtwork.title} - Image ${selectedImageIndex + 1}`}
                        className="w-full h-auto object-contain rounded-lg shadow-lg"
                      />
                      
                      {/* Image Navigation */}
                      {hasMultipleImages && (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 shadow-lg text-white"
                            onClick={() => navigateImage('prev', allImages.length)}
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 shadow-lg text-white"
                            onClick={() => navigateImage('next', allImages.length)}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                          
                          {/* Image Counter */}
                          <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                            {selectedImageIndex + 1} of {allImages.length}
                          </div>
                        </>
                      )}
                      
                      {/* Price Badge */}
                      {selectedArtwork.estimate_low && (
                        <Badge className="absolute top-4 right-4 bg-emerald-500 text-white shadow-lg">
                          <DollarSign className="w-3 h-3 mr-1" />
                          {formatEstimate(selectedArtwork.estimate_low, selectedArtwork.estimate_high)}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Image Thumbnails */}
                    {hasMultipleImages && (
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {allImages.map((img, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedImageIndex(index)}
                            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                              index === selectedImageIndex 
                                ? 'border-emerald-400 shadow-lg scale-105' 
                                : 'border-white/20 hover:border-emerald-400/50'
                            }`}
                          >
                            <SafeImage
                              src={img}
                              alt={`${selectedArtwork.title} - Thumbnail ${index + 1}`}
                              className="w-full h-full object-contain"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Details */}
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center gap-3 text-white/90 mb-4">
                        <div className="flex items-center gap-2">
                          <User className="w-5 h-5 text-emerald-400" />
                          <span className="font-light text-lg tracking-wide">{selectedArtwork.artistName}</span>
                        </div>
                        <span className="text-white/50">•</span>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-blue-400" />
                          <span className="text-lg font-light">{selectedArtwork.year}</span>
                        </div>
                      </div>
                      
                      {selectedArtwork.artistYears && (
                        <p className="text-white/70 italic mb-4 font-light">
                          {selectedArtwork.artistYears}
                        </p>
                      )}
                    </div>

                    {selectedArtwork.description && (
                      <div>
                        <h4 className="font-light text-white mb-2 tracking-wide">Description</h4>
                        <p className="text-white/70 leading-relaxed font-light">
                          {selectedArtwork.description}
                        </p>
                      </div>
                    )}

                    <div className="space-y-3">
                      {selectedArtwork.medium && (
                        <div className="flex items-center gap-2">
                          <Palette className="w-5 h-5 text-teal-400" />
                          <span className="text-white/80 font-light">
                            <strong className="font-normal">Medium:</strong> {selectedArtwork.medium}
                          </span>
                        </div>
                      )}
                      {selectedArtwork.dimensions && (
                        <div className="flex items-center gap-2">
                          <span className="text-white/80 font-light">
                            <strong className="font-normal">Dimensions:</strong> {selectedArtwork.dimensions}
                          </span>
                        </div>
                      )}
                                             {hasMultipleImages && (
                         <div className="flex items-center gap-2">
                           <Eye className="w-5 h-5 text-blue-400" />
                           <span className="text-white/80 font-light">
                             <strong className="font-normal">Images:</strong> {allImages.length} photos available
                           </span>
                         </div>
                       )}
                       

                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-light shadow-lg hover:shadow-xl transition-all duration-300 border-0 backdrop-blur-xl tracking-wide hover:scale-[1.02] hover:-translate-y-0.5">
                        <Heart className="w-4 h-4 mr-2" />
                        Add to Favorites
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default LivingGallery; 